import { formatPhoneNumber } from '../../helpers';
import iconInstallerAddress from '../../images/success/icon_installer_address.svg';
import iconInstallerEmail from '../../images/success/icon_installer_email.svg';
import iconInstallerPhone from '../../images/success/icon_installer_phone.svg';
import ratingStarEmpty from './../../images/success/rating_star_empty.svg';
import ratingStarFilled from './../../images/success/rating_star_filled.svg';
import ratingStarSemiFilled from './../../images/success/rating_star_semifilled.svg';
import { type CurrentInstaller } from '../types';

export interface InstallerData {
  name: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  email: string;
  phoneNumber: string;
  googleRating: number;
  googleRatingReviewCount: number;
}

export interface InstallerProps {
  installer: InstallerData;
}

type RatingStar = 'empty' | 'filled' | 'semiFilled';

export default function Installer({ installer }: InstallerProps) {
  return installer ? (
    <div className="bg-[#FAF6EF] rounded-[16px] p-6 md:p-8 md:max-w-[560px] mx-auto">
      <p className="text-[16px] short:!text-[18px] md:text-[24px]">
        {installer.name}
      </p>
      <InstallerRating rating={installer.googleRating}></InstallerRating>
      <div className="flex mt-8 items-start">
        <img
          src={iconInstallerAddress}
          alt=""
          className="w-6 md:w-8 mr-2 md:mr-3"
        />
        <p className="capitalize">
          {`${installer.address1 ?? ''}, ${
            installer.address2 ? installer.address2 + ', ' : ''
          }${installer.city ?? ''}, ${installer.state ?? ''} ${
            installer.zipCode ?? ''
          }${installer.country ? `, ${installer.country}` : ''}`}
        </p>
      </div>
      <div className="flex mt-6 items-center flex-wrap gap-y-2">
        <div className="flex mr-6">
          <div className="w-6 md:w-8 mr-2 md:mr-3 flex justify-center items-center">
            <img
              src={iconInstallerEmail}
              alt=""
              className="w-[18px] md:w-[23px]"
            />
          </div>
          <p>{installer.email}</p>
        </div>
        {installer.phoneNumber && (
          <div className="flex">
            <img
              src={iconInstallerPhone}
              alt=""
              className="mr-3 w-[18px] md:w-[25px]"
            />
            <p>{formatPhoneNumber(installer.phoneNumber)}</p>
          </div>
        )}
      </div>
    </div>
  ) : null;
}

export const InstallerRating = ({ rating }: { rating: number }) => {
  if (!rating) return <></>;
  const ratingStars: RatingStar[] = new Array(5).fill('empty');
  let filledStars = Math.floor(rating);
  if (filledStars > 5) filledStars = 5;
  for (let i = 0; i < filledStars; i++) {
    ratingStars[i] = 'filled';
  }
  if (rating > filledStars && filledStars !== 5)
    ratingStars[filledStars] = 'semiFilled';

  return (
    <div className="inline-flex h-fit items-center">
      <p className="text-[14px] pt-[3px] font-bold">{rating.toFixed(1)}</p>
      <div className="inline-flex ml-1">
        {ratingStars.map((star, index) => (
          <img
            key={index}
            alt=""
            className="h-4 w-4 ml-[4px]"
            src={
              star === 'empty'
                ? ratingStarEmpty
                : star === 'filled'
                ? ratingStarFilled
                : ratingStarSemiFilled
            }
          />
        ))}
      </div>
    </div>
  );
};

export const getCurrentInstallerData = (
  data: CurrentInstaller
): InstallerData => {
  return {
    name: data.profile?.name,
    address1: data.profile?.address1,
    address2: data.profile?.address2,
    city: data.profile?.city,
    state: data.profile?.state,
    zipCode: data.profile?.zip,
    country: data.profile?.country,
    email: data.profile?.email,
    phoneNumber: data.profile?.phone,
    googleRating: data.google_rating?.rating,
    googleRatingReviewCount: data.google_rating?.review_count,
  };
};
